import React from "react";
import { Box, Chip, Typography, alpha, useTheme } from "@mui/material";

/**
 * Pill-style category chips (All + template categories).
 * @param {{ categories: Array<{id:number,name:string,color?:string}>, value: string|number, onChange: (id: string) => void, includeAll?: boolean, lang?: object, emptyHint?: string }} props
 */
export default function CategoryFilterChips({
  categories = [],
  value,
  onChange,
  includeAll = true,
  lang,
  emptyHint,
}) {
  const theme = useTheme();
  const selected = value === "all" || value === "" ? "all" : String(value);

  const chipSx = (active) => ({
    fontWeight: 700,
    fontSize: "0.8125rem",
    height: 36,
    borderRadius: 999,
    px: 0.5,
    transition: "all 0.2s ease",
    borderWidth: 1.5,
    borderStyle: "solid",
    ...(active
      ? {
          bgcolor: theme.palette.mode === "dark" ? "#f5f5f5" : "#111827",
          color: theme.palette.mode === "dark" ? "#111827" : "#fff",
          borderColor: theme.palette.mode === "dark" ? "#f5f5f5" : "#111827",
          "&:hover": {
            bgcolor: theme.palette.mode === "dark" ? "#e5e5e5" : "#1f2937",
          },
        }
      : {
          bgcolor: alpha(theme.palette.background.paper, 0.9),
          color: "text.primary",
          borderColor: alpha(theme.palette.divider, 0.9),
          "&:hover": {
            borderColor: alpha(theme.palette.primary.main, 0.45),
            bgcolor: alpha(theme.palette.primary.main, 0.04),
          },
        }),
  });

  if (!categories.length && !includeAll) {
    return emptyHint ? (
      <Typography variant="body2" color="text.secondary">
        {emptyHint}
      </Typography>
    ) : null;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 1,
        py: 0.5,
      }}
    >
      {includeAll && (
        <Chip
          label={lang?.charactersFilterAll || "All"}
          onClick={() => onChange("all")}
          sx={chipSx(selected === "all")}
        />
      )}
      {categories.map((c) => {
        const id = String(c.id);
        const active = selected === id;
        return (
          <Chip
            key={c.id}
            label={c.name || `Category ${c.id}`}
            onClick={() => onChange(id)}
            sx={{
              ...chipSx(active),
              ...(active && c.color
                ? {
                    bgcolor: c.color,
                    borderColor: c.color,
                    color: "#fff",
                    "&:hover": { bgcolor: c.color, filter: "brightness(0.92)" },
                  }
                : {}),
            }}
          />
        );
      })}
    </Box>
  );
}
